import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

function getStockStatus(product) {
  if (product.quantity === null || product.quantity === undefined) return { icon: '➖', label: 'غير محدد', cls: 'untracked' };
  if (product.quantity === 0) return { icon: '❌', label: 'غير متوفر', cls: 'out' };
  if (product.quantity <= (product.lowStockThreshold || 5)) return { icon: '⚠️', label: 'كمية قليلة', cls: 'low' };
  return { icon: '✅', label: 'متوفر', cls: 'in' };
}

function ProductsPage({ idToken }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [editingQty, setEditingQty] = useState(null); // { id, value }
  const [saving, setSaving] = useState(null); // product id being saved
  const [editProduct, setEditProduct] = useState(null); // product being edited in modal
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        fetch(`${WORKER_URL}/api/products`, { headers: { 'Authorization': `Bearer ${idToken}` } }),
        fetch(`${WORKER_URL}/api/products/categories`, { headers: { 'Authorization': `Bearer ${idToken}` } }),
      ]);
      if (prodRes.ok) setProducts(await prodRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
    setLoading(false);
  }, [idToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveQuantity = async (id, value) => {
    setSaving(id);
    const qty = value === '' ? null : parseInt(value, 10);
    try {
      await fetch(`${WORKER_URL}/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ quantity: isNaN(qty) ? null : qty }),
      });
      setProducts((prev) => prev.map((p) => p.id === id ? { ...p, quantity: isNaN(qty) ? null : qty } : p));
    } catch (err) {
      console.error('Failed to save quantity:', err);
    }
    setSaving(null);
    setEditingQty(null);
  };

  const toggleActive = async (id, isActive) => {
    try {
      await fetch(`${WORKER_URL}/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ isActive: !isActive }),
      });
      setProducts((prev) => prev.map((p) => p.id === id ? { ...p, isActive: !isActive } : p));
    } catch (err) {
      console.error('Failed to toggle active:', err);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('حذف هذا المنتج؟')) return;
    try {
      await fetch(`${WORKER_URL}/api/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
  };

  // Filter products
  const parentCategories = categories.filter((c) => !c.parentId);

  const filtered = products.filter((p) => {
    // Category filter
    if (activeCategory !== 'all') {
      const cat = categories.find((c) => c.id === activeCategory);
      if (cat) {
        // If parent category selected, show all children too
        const childIds = categories.filter((c) => c.parentId === cat.id).map((c) => c.name);
        const matchNames = [cat.name, ...childIds];
        if (!matchNames.includes(p.category) && !matchNames.includes(p.parentCategory)) return false;
      }
    }
    // Search
    if (search) {
      const q = search.toLowerCase();
      const matches = (p.name || '').toLowerCase().includes(q)
        || (p.nameAr || '').includes(search)
        || (p.brand || '').toLowerCase().includes(q)
        || (p.category || '').toLowerCase().includes(q)
        || (p.id || '').toLowerCase().includes(q);
      if (!matches) return false;
    }
    // Stock filter
    if (stockFilter !== 'all') {
      const status = getStockStatus(p);
      if (stockFilter === 'in' && status.cls !== 'in') return false;
      if (stockFilter === 'out' && status.cls !== 'out') return false;
      if (stockFilter === 'low' && status.cls !== 'low') return false;
      if (stockFilter === 'untracked' && status.cls !== 'untracked') return false;
    }
    return true;
  });

  // Stats
  const totalProducts = products.length;
  const inStock = products.filter((p) => getStockStatus(p).cls === 'in').length;
  const outOfStock = products.filter((p) => getStockStatus(p).cls === 'out').length;
  const lowStock = products.filter((p) => getStockStatus(p).cls === 'low').length;
  const untracked = products.filter((p) => getStockStatus(p).cls === 'untracked').length;

  if (loading) {
    return <div className="sd-stats-loading">جاري تحميل المنتجات...</div>;
  }

  return (
    <div className="sp-container">
      {/* Header */}
      <div className="sp-header">
        <h2 className="sp-title">📦 المنتجات ({totalProducts})</h2>
        <button className="sp-add-btn" onClick={() => setShowAddModal(true)}>+ أضف منتج</button>
      </div>

      {/* Stats Bar */}
      <div className="sp-stats-bar">
        <span className="sp-stat sp-stat-in" onClick={() => setStockFilter(stockFilter === 'in' ? 'all' : 'in')}>✅ متوفر: {inStock}</span>
        <span className="sp-stat sp-stat-low" onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}>⚠️ قليل: {lowStock}</span>
        <span className="sp-stat sp-stat-out" onClick={() => setStockFilter(stockFilter === 'out' ? 'all' : 'out')}>❌ نفذ: {outOfStock}</span>
        <span className="sp-stat sp-stat-untracked" onClick={() => setStockFilter(stockFilter === 'untracked' ? 'all' : 'untracked')}>➖ غير محدد: {untracked}</span>
      </div>

      {/* Category Tabs */}
      <div className="sp-category-tabs">
        <button
          className={`sp-cat-tab ${activeCategory === 'all' ? 'active' : ''}`}
          onClick={() => setActiveCategory('all')}
        >الكل</button>
        {parentCategories.map((cat) => (
          <button
            key={cat.id}
            className={`sp-cat-tab ${activeCategory === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(activeCategory === cat.id ? 'all' : cat.id)}
          >{cat.nameAr || cat.name}</button>
        ))}
      </div>

      {/* Search */}
      <div className="sp-search-row">
        <input
          className="sp-search-input"
          type="text"
          placeholder="🔍 بحث بالاسم أو الماركة أو رقم القطعة..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {(search || stockFilter !== 'all' || activeCategory !== 'all') && (
          <button className="sp-clear-filters" onClick={() => { setSearch(''); setStockFilter('all'); setActiveCategory('all'); }}>
            مسح الفلاتر
          </button>
        )}
        <span className="sp-result-count">{filtered.length} منتج</span>
      </div>

      {/* Product List */}
      <div className="sp-product-list">
        {filtered.map((p) => {
          const stock = getStockStatus(p);
          const isEditing = editingQty && editingQty.id === p.id;

          return (
            <div key={p.id} className={`sp-product-card ${!p.isActive ? 'sp-inactive' : ''}`}>
              <div className="sp-product-img">
                {p.image ? (
                  <img src={p.image} alt={p.name} loading="lazy" />
                ) : (
                  <div className="sp-no-img">📦</div>
                )}
              </div>

              <div className="sp-product-info">
                <div className="sp-product-name">{p.name}</div>
                {p.nameAr && <div className="sp-product-name-ar">{p.nameAr}</div>}
                <div className="sp-product-meta">
                  <span className="sp-product-category">{p.categoryAr || p.category}</span>
                  {p.brand && <span className="sp-product-brand">{p.brand}</span>}
                </div>
                {!p.isActive && <span className="sp-badge-inactive">معطّل</span>}
              </div>

              <div className="sp-product-stock">
                <div className={`sp-stock-badge sp-stock-${stock.cls}`}>
                  {stock.icon} {stock.label}
                </div>
                <div className="sp-qty-row">
                  <label>الكمية:</label>
                  {isEditing ? (
                    <div className="sp-qty-edit">
                      <input
                        type="number"
                        className="sp-qty-input"
                        value={editingQty.value}
                        onChange={(e) => setEditingQty({ id: p.id, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveQuantity(p.id, editingQty.value);
                          if (e.key === 'Escape') setEditingQty(null);
                        }}
                        autoFocus
                      />
                      <button
                        className="sp-qty-save"
                        onClick={() => saveQuantity(p.id, editingQty.value)}
                        disabled={saving === p.id}
                      >{saving === p.id ? '...' : '💾'}</button>
                      <button className="sp-qty-cancel" onClick={() => setEditingQty(null)}>✕</button>
                    </div>
                  ) : (
                    <span
                      className="sp-qty-value"
                      onClick={() => setEditingQty({ id: p.id, value: p.quantity ?? '' })}
                      title="اضغط لتعديل الكمية"
                    >
                      {p.quantity !== null && p.quantity !== undefined ? p.quantity : '—'}
                    </span>
                  )}
                </div>
              </div>

              <div className="sp-product-actions">
                <button className="sp-action-btn" onClick={() => setEditProduct(p)} title="تعديل">✏️</button>
                <button
                  className={`sp-action-btn ${p.isActive ? '' : 'sp-action-activate'}`}
                  onClick={() => toggleActive(p.id, p.isActive)}
                  title={p.isActive ? 'تعطيل' : 'تفعيل'}
                >{p.isActive ? '🔴' : '🟢'}</button>
                <button className="sp-action-btn sp-action-delete" onClick={() => deleteProduct(p.id)} title="حذف">🗑️</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      {editProduct && (
        <ProductEditModal
          product={editProduct}
          idToken={idToken}
          onClose={() => setEditProduct(null)}
          onSave={(updated) => {
            setProducts((prev) => prev.map((p) => p.id === updated.id ? { ...p, ...updated } : p));
            setEditProduct(null);
          }}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <ProductAddModal
          idToken={idToken}
          onClose={() => setShowAddModal(false)}
          onSave={(newProduct) => {
            setProducts((prev) => [...prev, newProduct]);
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Edit Modal ───

function ProductEditModal({ product, idToken, onClose, onSave }) {
  const [form, setForm] = useState({
    name: product.name || '',
    nameAr: product.nameAr || '',
    category: product.category || '',
    categoryAr: product.categoryAr || '',
    brand: product.brand || '',
    quantity: product.quantity ?? '',
    price: product.price ?? '',
    lowStockThreshold: product.lowStockThreshold ?? 5,
    isActive: product.isActive ?? true,
    subtitle: product.subtitle || '',
    subtitleAr: product.subtitleAr || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const updates = {
      ...form,
      quantity: form.quantity === '' ? null : parseInt(form.quantity, 10),
      price: form.price === '' ? null : parseFloat(form.price),
      lowStockThreshold: parseInt(form.lowStockThreshold, 10) || 5,
    };
    try {
      const res = await fetch(`${WORKER_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        onSave(data.product || { ...product, ...updates });
      }
    } catch (err) {
      console.error('Failed to save product:', err);
    }
    setSaving(false);
  };

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="sp-modal-overlay" onClick={onClose}>
      <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sp-modal-header">
          <h3>تعديل المنتج</h3>
          <button className="sp-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sp-modal-body">
          {product.image && (
            <div className="sp-modal-img">
              <img src={product.image} alt={product.name} />
            </div>
          )}

          <div className="sp-form-row">
            <label>الاسم (EN)</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="sp-form-row">
            <label>الاسم (AR)</label>
            <input value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} dir="rtl" />
          </div>
          <div className="sp-form-row">
            <label>الوصف (EN)</label>
            <input value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} />
          </div>
          <div className="sp-form-row">
            <label>الوصف (AR)</label>
            <input value={form.subtitleAr} onChange={(e) => set('subtitleAr', e.target.value)} dir="rtl" />
          </div>
          <div className="sp-form-grid">
            <div className="sp-form-row">
              <label>القسم</label>
              <input value={form.category} onChange={(e) => set('category', e.target.value)} />
            </div>
            <div className="sp-form-row">
              <label>الماركة</label>
              <input value={form.brand} onChange={(e) => set('brand', e.target.value)} />
            </div>
          </div>
          <div className="sp-form-grid">
            <div className="sp-form-row">
              <label>الكمية</label>
              <input type="number" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} placeholder="فارغ = غير محدد" />
            </div>
            <div className="sp-form-row">
              <label>السعر (ريال)</label>
              <input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="فارغ = غير محدد" />
            </div>
          </div>
          <div className="sp-form-grid">
            <div className="sp-form-row">
              <label>حد التنبيه</label>
              <input type="number" value={form.lowStockThreshold} onChange={(e) => set('lowStockThreshold', e.target.value)} />
            </div>
            <div className="sp-form-row sp-form-toggle">
              <label>مفعّل في واتساب</label>
              <button
                className={`sp-toggle ${form.isActive ? 'sp-toggle-on' : ''}`}
                onClick={() => set('isActive', !form.isActive)}
              >{form.isActive ? '✅ مفعّل' : '❌ معطّل'}</button>
            </div>
          </div>
        </div>

        <div className="sp-modal-footer">
          <button className="sp-btn-cancel" onClick={onClose}>إلغاء</button>
          <button className="sp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'جاري الحفظ...' : '💾 حفظ'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Modal ───

function ProductAddModal({ idToken, onClose, onSave }) {
  const [form, setForm] = useState({
    id: '',
    name: '',
    nameAr: '',
    category: '',
    categoryAr: '',
    brand: '',
    quantity: '',
    price: '',
    subtitle: '',
    subtitleAr: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.name) { setError('الاسم مطلوب'); return; }
    const productId = form.id || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    setSaving(true);
    setError('');
    const product = {
      id: productId,
      name: form.name,
      nameAr: form.nameAr,
      category: form.category,
      categoryAr: form.categoryAr,
      brand: form.brand,
      subtitle: form.subtitle,
      subtitleAr: form.subtitleAr,
      quantity: form.quantity === '' ? null : parseInt(form.quantity, 10),
      price: form.price === '' ? null : parseFloat(form.price),
      lowStockThreshold: 5,
      isActive: true,
      image: '',
      imageOriginal: '',
      parentCategory: '',
      parentCategoryAr: '',
      description: '',
      descriptionAr: '',
      specs: {},
      partNumbers: [],
      sourceUrl: '',
    };

    try {
      const res = await fetch(`${WORKER_URL}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify(product),
      });
      if (res.ok) {
        onSave(product);
      } else {
        const data = await res.json();
        setError(data.error || 'فشل الإضافة');
      }
    } catch (err) {
      setError('خطأ في الاتصال');
    }
    setSaving(false);
  };

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="sp-modal-overlay" onClick={onClose}>
      <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sp-modal-header">
          <h3>إضافة منتج جديد</h3>
          <button className="sp-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sp-modal-body">
          {error && <div className="sp-error">{error}</div>}
          <div className="sp-form-row">
            <label>الاسم (EN) *</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="مثال: Optibelt RED POWER 3" />
          </div>
          <div className="sp-form-row">
            <label>الاسم (AR)</label>
            <input value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} dir="rtl" />
          </div>
          <div className="sp-form-grid">
            <div className="sp-form-row">
              <label>القسم</label>
              <input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="V Belts" />
            </div>
            <div className="sp-form-row">
              <label>الماركة</label>
              <input value={form.brand} onChange={(e) => set('brand', e.target.value)} placeholder="Optibelt" />
            </div>
          </div>
          <div className="sp-form-grid">
            <div className="sp-form-row">
              <label>الكمية</label>
              <input type="number" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} />
            </div>
            <div className="sp-form-row">
              <label>السعر (ريال) — اختياري</label>
              <input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} />
            </div>
          </div>
          <div className="sp-form-row">
            <label>معرّف المنتج (ID)</label>
            <input value={form.id} onChange={(e) => set('id', e.target.value)} placeholder="يُولّد تلقائياً من الاسم" style={{ direction: 'ltr' }} />
          </div>
        </div>

        <div className="sp-modal-footer">
          <button className="sp-btn-cancel" onClick={onClose}>إلغاء</button>
          <button className="sp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'جاري الإضافة...' : '+ إضافة'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductsPage;
